import { useMemo } from "react"

import { useTheme } from "@emotion/react"
import { IconButton, List, Typography } from "@mui/material"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { KeyboardArrowLeftIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { ScrollableLayout } from "../../layouts/ScrollableLayout"

import { HierarchicalListItem } from "../HierarchicalListItem"
import { L2RTextField } from "../TextField"

import * as Styled from "./HierarchicalList.styled"

const getFinalTypes = (items = []) =>
    items.flatMap(item => item.children?.length ? getFinalTypes(item.children) : [item])

export const HierarchicalList = (props) => {
    const {
        className,
        data,
        searchData,
        defaultIcon,
        handleBack,
        title,
        onCancel,
        onClick,
        ...listProps
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const theme = useTheme()

    const finalTypes = useMemo(() => getFinalTypes(searchData ?? data), [searchData, data])

    return <Styled.Container className={className}>
        <Styled.TitleContainer>
            {handleBack ? <IconButton onClick={handleBack}>
                <KeyboardArrowLeftIcon/>
            </IconButton> : <Styled.BackButtonPlaceholder/>}
            <Typography variant="Desktop & Mobile/Heading/H3">{title}</Typography>
        </Styled.TitleContainer>
        <Styled.Autocomplete
            options={finalTypes}
            value={null}
            blurOnSelect
            clearOnBlur
            getOptionLabel={option => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={params =>
                <L2RTextField
                    {...params}
                    autoFocus
                    placeholder={t(I18N_NAMESPACE, "components.hierarchicalList.searchPlaceholder")}
                    slotProps={{
                        input: params.InputProps,
                        htmlInput: params.inputProps,
                    }}
                />}
            renderOption={(optionProps, option) => {
                const { key, ...otherOptionProps } = optionProps
                return <Styled.Option key={key} {...otherOptionProps}>
                    {option.icon && <Styled.OptionImage
                        src={option.icon}
                        onError={e => e.target.src = defaultIcon}
                    />}
                    <Typography variant="Desktop & Mobile/Body/Regular">{option.label}</Typography>
                </Styled.Option>
            }}
            slotProps={{
                paper: {
                    sx: {
                        boxShadow: theme.customShadow("LCS/Shadow-dropdown"),
                        borderRadius: theme.spacing(3),
                        marginTop: theme.spacing(1),
                    },
                },
            }}
            onChange={(_, option) => option && onClick(option.value)}
        />
        <Styled.Form>
            <ScrollableLayout
                content={
                    <List {...listProps}>
                        {
                            data.map(listItem =>
                                <HierarchicalListItem
                                    {...listItem}
                                    defaultIcon={defaultIcon}
                                    key={listItem.value}
                                    onClick={onClick}
                                />)
                        }
                    </List>
                }
                buttons={
                    <Styled.CancelButton
                        id="list-cancel-button"
                        onClick={onCancel}
                        variant="tertiary"
                    >
                        {t(I18N_NAMESPACE, "components.hierarchicalList.cancel")}
                    </Styled.CancelButton>
                }
            />
        </Styled.Form>
    </Styled.Container>
}

HierarchicalList.propTypes = {
    className: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.object),
    searchData: PropTypes.arrayOf(PropTypes.object),
    defaultIcon: PropTypes.string.isRequired,
    handleBack: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
}

